import { useEffect, useState, createContext, createElement } from "react";
import * as signalR from "@microsoft/signalr";
import { useKeycloak } from "@react-keycloak/web";
import { getBaseURL } from "./helpers";

// Create a new context
export const SignalRContext = createContext<signalR.HubConnection | null>(null);

// Create a provider component
export const SignalRProvider = ({ children }: any) => {
    const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
    const [retryCount, setRetryCount] = useState(0);
    const { keycloak } = useKeycloak();

    const createConnection = async () => {
        if (keycloak.token && keycloak.authenticated) {
            console.log("Creating SignalR connection with token:", keycloak.token ? "present" : "missing");
            console.log("Keycloak authenticated:", keycloak.authenticated);
            console.log("Base URL:", getBaseURL());
            
            // Check if token is expired or about to expire
            if (keycloak.isTokenExpired()) {
                console.warn("Token is expired, attempting to refresh...");
                try {
                    await keycloak.updateToken(30);
                    console.log("Token refreshed successfully");
                } catch (error) {
                    console.error("Failed to refresh token:", error);
                    return;
                }
            }
            
            // Test backend connectivity first
            try {
                const testUrl = `${getBaseURL()}/monitoringHub/negotiate?negotiateVersion=1`;
                console.log("Testing backend connectivity:", testUrl);
                
                const response = await fetch(testUrl, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${keycloak.token}`,
                        'p365-tenant-url-identifier': window.location.pathname.split('/')[1],
                        'Content-Type': 'application/json'
                    }
                });
                
                if (!response.ok) {
                    console.error(`Backend test failed: ${response.status} ${response.statusText}`);
                    if (response.status === 401) {
                        console.error("Backend returned 401 - authentication issue");
                    }
                    return;
                }
                
                console.log("Backend connectivity test passed");
            } catch (error) {
                console.error("Backend connectivity test failed:", error);
                return;
            }
            
            const tenantIdentifier = window.location.pathname.split('/')[1];
            console.log("Tenant identifier:", tenantIdentifier);
            
            const newConnection = new signalR.HubConnectionBuilder()
                .withUrl(`${getBaseURL()}/monitoringHub`, {
                    accessTokenFactory: () => {
                        console.log("Access token factory called, token present:", !!keycloak.token);
                        if (!keycloak.token) {
                            throw new Error("No access token available");
                        }
                        return keycloak.token;
                    },
                    headers: { 
                        "p365-tenant-url-identifier": tenantIdentifier,
                        "Authorization": `Bearer ${keycloak.token}`
                    }
                })
                .withAutomaticReconnect()
                .build();

            setConnection(newConnection);
        } else {
            console.warn("Cannot create SignalR connection:", {
                hasToken: !!keycloak.token,
                isAuthenticated: keycloak.authenticated,
                tokenExpired: keycloak.isTokenExpired?.() || false
            });
        }
    };

    useEffect(() => {
        if (!connection) {
            createConnection().catch(err => console.error("Failed to create connection:", err));
        }

        return () => {
            if (connection) {
                connection.stop();
            }
        };
    }, [connection, keycloak.token]);

    useEffect(() => {
        if (connection) {
            const startConnection = async () => {
                try {
                    console.log("Attempting to start SignalR connection...");
                    console.log("Connection URL:", `${getBaseURL()}/monitoringHub`);
                    console.log("Headers:", { 
                        "p365-tenant-url-identifier": window.location.pathname.split('/')[1],
                        "Authorization": `Bearer ${keycloak.token?.substring(0, 20)}...`
                    });
                    
                    // Additional check before starting
                    if (!keycloak.token || keycloak.isTokenExpired()) {
                        console.warn("Token missing or expired, attempting refresh...");
                        await keycloak.updateToken(30);
                    }
                    
                    await connection.start();
                    console.log("SignalR connection started successfully");
                    
                    // Reset retry count on successful connection
                    setRetryCount(0);
                    
                    // Trigger a custom event to notify components that connection is ready
                    window.dispatchEvent(new CustomEvent('signalr-connected'));
                } catch (err: any) {
                    console.error("Connection start failed: ", err);
                    
                    // Check if it's a 401 error specifically
                    if (err.message?.includes('401') || err.statusCode === 401) {
                        console.error("401 Unauthorized - Token or authentication issue");
                        console.log("Token info:", {
                            hasToken: !!keycloak.token,
                            isAuthenticated: keycloak.authenticated,
                            tokenExpired: keycloak.isTokenExpired?.() || false,
                            tokenParsed: keycloak.tokenParsed
                        });
                        
                        // Retry with token refresh if retry count is low
                        if (retryCount < 2) {
                            console.log(`Retrying connection (attempt ${retryCount + 1}/2)...`);
                            setRetryCount(prev => prev + 1);
                            
                            try {
                                await keycloak.updateToken(30);
                                console.log("Token refreshed, recreating connection...");
                                await connection.stop();
                                await createConnection();
                                return;
                            } catch (refreshError) {
                                console.error("Failed to refresh token:", refreshError);
                            }
                        }
                    }
                    
                    window.dispatchEvent(new CustomEvent('signalr-connection-failed', { detail: err }));
                }
            };

            startConnection();

            // Add connection state change handlers
            connection.onclose((error) => {
                console.log("SignalR connection closed", error);
            });

            connection.onreconnecting((error) => {
                console.log("SignalR reconnecting", error);
            });

            connection.onreconnected((connectionId) => {
                console.log("SignalR reconnected", connectionId);
            });

            return () => {
                connection.stop();
            };
        }
    }, [connection]);

    useEffect(() => {
        keycloak.onAuthRefreshSuccess = async () => {
            console.log("Auth refresh success - recreating SignalR connection");

            if (connection) {
                await connection.stop();
            }
            createConnection().catch(err => console.error("Failed to recreate connection after auth refresh:", err));
        };

        keycloak.onAuthError = (error) => {
            console.error("Keycloak auth error:", error);
            window.dispatchEvent(new CustomEvent('signalr-connection-failed', { detail: error }));
        };

        keycloak.onAuthLogout = () => {
            console.log("Keycloak logout - stopping SignalR connection");
            if (connection) {
                connection.stop();
            }
        };
    }, [keycloak, connection]);

    return createElement(SignalRContext.Provider, { value: connection }, children);
};

